import PageHeader from "@/components/PageHeader";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { Users, Heart, Home as HomeIcon, GraduationCap, Briefcase, ArrowRight, Sparkles } from "lucide-react";

const BOOKING_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";
const OUTLOOK_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";
const INTERNSHIP_FORM_URL = "https://forms.cloud.microsoft/Pages/ResponsePage.aspx?id=8b6jusaYg0KZdna1WthYypvbokLA_ZtCmYbeFJI7vY1UM0kyVkc3Sko1SkwyNDQyWkRYWENPQktTWi4u&origin=Invitation&channel=1";

const PAGE_IMG = "https://static.prod-images.emergentagent.com/jobs/45c13cc0-c545-4ce4-a5ad-55fc17f653c4/images/aeae3fbc16d5d58644a3f7af64679b3b75d25173e56b234801f6f4b79de53aeb.png";

const clientServices = [
  {
    icon: Heart,
    title: "Individual Therapy",
    desc: "Support for anxiety, overthinking, life transitions, and building confidence. We use CBT, mindfulness, and skills-based approaches tailored to your story.",
    points: ["Anxiety & overthinking", "Perfectionism", "Life transitions", "Self-worth & confidence"],
  },
  {
    icon: Users,
    title: "Couples Therapy",
    desc: "Improve communication, rebuild trust, and break unhealthy relationship patterns. We help couples create new, healthier ways of relating.",
    points: ["Communication coaching", "Rebuilding trust", "Conflict patterns", "Reconnection"],
  },
  {
    icon: HomeIcon,
    title: "Family Therapy",
    desc: "Strengthen family dynamics, improve understanding, and create healthier connections across generations.",
    points: ["Parent-child dynamics", "Blended families", "Adolescent support", "Family transitions"],
  },
  {
    icon: Sparkles,
    title: "Life Skills for Autistic Adults",
    desc: "Neurodivergent-affirming life-skills coaching for autistic adults. Practical, identity-affirming work that builds independence and confidence — without masking.",
    points: ["Executive function & routines", "Sensory regulation & burnout recovery", "Workplace & social navigation", "Late-diagnosis processing"],
    spotlight: true,
  },
];

const clinicianServices = [
  {
    icon: GraduationCap,
    title: "LMSW Supervision",
    desc: "Guidance, structure, and support as you work toward LCSW licensure. Build clinical confidence with weekly individual or group supervision.",
    cta: "Start Supervision",
  },
  {
    icon: Briefcase,
    title: "Internship Program",
    desc: "Hands-on clinical training and real-world skill development for graduate students pursuing careers in counseling and social work.",
    cta: "Apply Now",
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="Therapy Services in Texas — Individual, Couples, Autism & LMSW Supervision"
        description="Counseling and coaching across Texas: individual & couples therapy, family therapy, life skills for autistic adults, and LMSW → LCSW supervision with Sophia Dinwiddie-Donald, LCSW-S."
        path="/services"
      />
      <PageHeader
        overline="Our Services"
        title={<>Care designed for <em className="italic text-terracotta">real life.</em></>}
        subtitle="We specialize in helping individuals, couples, families, and autistic adults navigate change, rebuild stability, and move forward with confidence — one pillar at a time."
        image={PAGE_IMG}
      />

      {/* FOR CLIENTS */}
      <section className="py-20 md:py-28 bg-bone">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="overline mb-5">For Clients</p>
          <h2 className="font-display text-3xl md:text-5xl text-forest font-medium tracking-tight max-w-2xl">
            Therapy that fits the season <em className="italic text-terracotta">you're in.</em>
          </h2>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {clientServices.map((s) => {
              const Icon = s.icon;
              return (
                <article
                  key={s.title}
                  data-testid={`client-service-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                  className={`rounded-2xl p-8 hover:-translate-y-1 hover:shadow-xl transition-all duration-300 flex flex-col ${
                    s.spotlight
                      ? "bg-terracotta text-bone border border-terracotta hover:border-bone/40"
                      : "bg-surface border border-sage/25 hover:border-terracotta/40"
                  }`}
                >
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center mb-6 ${
                    s.spotlight ? "bg-bone/15 text-bone" : "bg-forest/5 text-forest"
                  }`}>
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className={`font-display text-2xl md:text-3xl font-medium mb-4 ${s.spotlight ? "text-bone" : "text-forest"}`}>{s.title}</h3>
                  <p className={`leading-relaxed mb-6 ${s.spotlight ? "text-bone/85" : "text-ink/70"}`}>{s.desc}</p>
                  <ul className={`space-y-2 mb-8 text-sm ${s.spotlight ? "text-bone/90" : "text-ink/75"}`}>
                    {s.points.map((p) => (
                      <li key={p} className="flex items-center gap-2">
                        <span className={`h-1 w-4 rounded-full ${s.spotlight ? "bg-bone/60" : "bg-terracotta"}`} />
                        {p}
                      </li>
                    ))}
                  </ul>
                  {s.spotlight ? (
                    <Link
                      to="/autism-life-skills"
                      data-testid="services-autism-learn-more"
                      className="mt-auto inline-flex items-center font-medium link-underline text-bone"
                    >
                      Learn more <ArrowRight size={14} className="ml-1" />
                    </Link>
                  ) : (
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-auto inline-flex items-center font-medium link-underline text-forest"
                    >
                      Learn more <ArrowRight size={14} className="ml-1" />
                    </a>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* FOR CLINICIANS — Supervision Spotlight */}
      <section id="supervision" className="py-20 md:py-28 bg-surface scroll-mt-24" data-testid="supervision-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-14">
            <div className="lg:col-span-7">
              <p className="overline mb-5">For Therapists & Interns</p>
              <h2 className="font-display text-3xl md:text-5xl text-forest font-medium tracking-tight">
                LMSW → LCSW <em className="italic text-terracotta">Supervision</em> by an LCSW-S.
              </h2>
              <p className="mt-5 text-ink/75 leading-relaxed text-lg">
                Structured, ethical, growth-focused supervision toward Texas LCSW licensure — led by Sophia Dinwiddie-Donald, MSSW, LCSW-S. Build clinical confidence, sharpen your diagnostic and case-conceptualization skills, and develop your therapist identity in a supportive, accountable container.
              </p>
              <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-ink/80">
                {[
                  "Weekly individual or group supervision",
                  "Texas LCSW board hour documentation",
                  "Clinical case consultation",
                  "Ethics & risk navigation",
                  "Identity & burnout support",
                  "Pathway planning to licensure",
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3">
                    <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-terracotta shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Link
                  to="/lcsw-supervision"
                  data-testid="supervision-start-btn"
                  className="inline-flex items-center justify-center bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-7 py-3.5 font-medium transition-colors"
                >
                  Learn About Supervision
                </Link>
                <a
                  href="https://sophia-dinwiddie-donald.clientsecure.me/request/clinician"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center bg-transparent border border-forest text-forest hover:bg-forest hover:text-bone rounded-full px-7 py-3.5 font-medium transition-colors"
                >
                  Request Consultation
                </a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                <div className="absolute -top-3 -left-3 w-full h-full bg-sage/40 rounded-2xl rotate-[-2deg]" />
                <div className="relative bg-forest text-bone rounded-2xl p-9 md:p-10">
                  <p className="overline text-terracotta mb-3">By the Numbers</p>
                  <p className="font-display text-5xl md:text-6xl font-semibold leading-none">100<span className="text-terracotta">+</span></p>
                  <p className="mt-2 text-bone/80 text-sm">supervision hours typically required by the Texas State Board of Social Worker Examiners for LCSW.</p>
                  <hr className="my-7 border-bone/15" />
                  <p className="text-bone/85 leading-relaxed text-sm">
                    Whether you're just starting your supervision hours or transferring from another supervisor, we'll create a plan that fits your pace and goals.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Internship */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {clinicianServices.filter((s) => s.title !== "LMSW Supervision").map((s) => {
              const Icon = s.icon;
              const isInternship = s.title === "Internship Program";
              const ctaLabel = isInternship ? "Apply for Internship Placement" : s.cta;
              return (
                <article
                  key={s.title}
                  data-testid={`clinician-service-${s.title.toLowerCase().replace(/\s/g, "-")}`}
                  className="bg-bone border border-sage/25 rounded-2xl p-10 md:p-12 flex flex-col justify-between min-h-[240px] hover:border-terracotta/40 hover:shadow-xl transition-all duration-300"
                >
                  <div>
                    <div className="h-12 w-12 rounded-full bg-forest/5 flex items-center justify-center text-forest mb-6">
                      <Icon size={22} strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl text-forest font-medium mb-3">{s.title}</h3>
                    <p className="text-ink/70 leading-relaxed">{s.desc}</p>
                  </div>
                  {isInternship ? (
                    <a
                      href={INTERNSHIP_FORM_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="internship-apply-btn"
                      className="mt-7 inline-flex items-center justify-center bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-7 py-3.5 font-medium transition-colors w-fit"
                    >
                      {ctaLabel} <ArrowRight size={14} className="ml-2" />
                    </a>
                  ) : (
                    <Link
                      to="/contact"
                      className="mt-7 inline-flex items-center text-forest font-medium link-underline w-fit"
                    >
                      {ctaLabel} <ArrowRight size={14} className="ml-1" />
                    </Link>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
            Not sure which service fits? <em className="italic text-terracotta">Let's talk.</em>
          </h2>
          <p className="mt-5 text-ink/70 max-w-xl mx-auto">
            A free 15-minute consultation helps us understand your goals and recommend the right path forward.
          </p>
          <a
            href={OUTLOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-cta-btn"
            className="mt-8 inline-flex items-center bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-8 py-4 font-medium transition-colors"
          >
            Book a free consultation
          </a>
        </div>
      </section>
    </div>
  );
}
